/*
 * useQrWaiterCallRealtime
 *
 * Realtime listener for the QR "Ofisanti çağır" feature. Subscribes to the
 * public Reverb/Pusher channel `restaurant.{rid}.waiter-calls` so the
 * customer instantly sees when a waiter acknowledges or resolves their call.
 *
 * Events listened on:
 *   `.waiter-call.status-changed`  → relayed via `onStatusChanged`
 *
 * We intentionally do NOT listen for `.waiter.called` on the customer side —
 * that event is for the POS dashboard. The customer creates the call via
 * the POST endpoint and the response already includes the `call_id`, so the
 * UI is in sync immediately without needing the broadcast.
 *
 * When the websocket connection drops we fall back to polling
 * `/api/qr/{token}/call-waiter/active` every `pollIntervalMs` (default 8s)
 * so the customer's status indicator never goes stale.
 */

import { useCallback, useEffect, useRef } from 'react';
import echo from '../services/echo';
import { QrWaiterCallStatusChangedEvent } from '../api/qrWaiterCall';

export interface UseQrWaiterCallRealtimeOptions {
  /** QR uniqueUrl for the polling fallback. */
  uniqueUrl: string | null | undefined;
  /** Restaurant id required for the channel name. Hook is a no-op without it. */
  restaurantId: number | null | undefined;
  /**
   * The currently-active call_id, used to filter out events for *other*
   * tables on the same restaurant. Pass `null` when no call is active —
   * the hook still listens so we don't miss the very first event after
   * a `POST /call-waiter`.
   */
  activeCallId: number | null;
  /**
   * Called whenever a `.waiter-call.status-changed` event arrives that
   * matches our active call. Parent uses this for optimistic state
   * updates (no extra HTTP round-trip needed).
   */
  onStatusChanged: (event: QrWaiterCallStatusChangedEvent) => void;
  /**
   * Called on websocket (re)connect and on every polling tick when the
   * websocket is unavailable. Parent refetches `/active` to reconcile.
   */
  onRefresh: () => void;
  /** Polling interval used when the websocket is down. Default 8s. */
  pollIntervalMs?: number;
  /** Disable the hook entirely (e.g. invalid QR page). */
  disabled?: boolean;
}

export function useQrWaiterCallRealtime(opts: UseQrWaiterCallRealtimeOptions): void {
  const {
    uniqueUrl,
    restaurantId,
    activeCallId,
    onStatusChanged,
    onRefresh,
    pollIntervalMs = 8000,
    disabled = false,
  } = opts;

  // Stable refs so the parent's lambdas don't re-trigger the effect.
  const onStatusChangedRef = useRef(onStatusChanged);
  const onRefreshRef = useRef(onRefresh);
  const activeCallIdRef = useRef(activeCallId);
  useEffect(() => {
    onStatusChangedRef.current = onStatusChanged;
  }, [onStatusChanged]);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);
  useEffect(() => {
    activeCallIdRef.current = activeCallId;
  }, [activeCallId]);

  const handleStatusChanged = useCallback((payload: any) => {
    if (!payload || typeof payload !== 'object') return;
    const ev = payload as QrWaiterCallStatusChangedEvent;
    // Only react to events for *our* active call. We compare on call_id —
    // matching by table_id is also possible but call_id is more precise
    // when one customer cancels and immediately re-creates a new call.
    const currentCallId = activeCallIdRef.current;
    if (currentCallId == null) {
      // No locally-tracked call — still let the parent decide (could be a
      // brand-new call we just optimistically created without yet knowing
      // its id).
      onStatusChangedRef.current(ev);
      return;
    }
    if (Number(ev.call_id) === Number(currentCallId)) {
      onStatusChangedRef.current(ev);
    }
  }, []);

  useEffect(() => {
    if (disabled) return;
    if (!uniqueUrl) return;

    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => {
        onRefreshRef.current();
      }, pollIntervalMs);
    };
    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    // ---------- Subscribe to the per-restaurant public channel ----------
    let channelName: string | null = null;
    if (restaurantId) {
      channelName = `restaurant.${restaurantId}.waiter-calls`;
      try {
        const ch = echo.channel(channelName);
        // The backend broadcasts via the dotted name (`broadcastAs`).
        ch.listen('.waiter-call.status-changed', handleStatusChanged);
      } catch {
        /* channel reject — fall through to polling */
      }
    }

    // ---------- Connection state -> polling fallback ----------
    const connector = (echo as any).connector?.pusher;
    const onConnected = () => {
      stopPolling();
      // Reconciliation pull on (re)connect.
      onRefreshRef.current();
    };
    const onDisconnected = () => {
      startPolling();
    };

    if (connector?.connection) {
      connector.connection.bind('connected', onConnected);
      connector.connection.bind('disconnected', onDisconnected);
      connector.connection.bind('unavailable', onDisconnected);
      connector.connection.bind('failed', onDisconnected);
      if (connector.connection.state !== 'connected') {
        startPolling();
      }
    } else {
      // No websocket available at all — pure polling.
      startPolling();
    }

    return () => {
      stopPolling();
      try {
        if (channelName) echo.leaveChannel(channelName);
      } catch {
        /* ignore */
      }
      if (connector?.connection) {
        try {
          connector.connection.unbind('connected', onConnected);
          connector.connection.unbind('disconnected', onDisconnected);
          connector.connection.unbind('unavailable', onDisconnected);
          connector.connection.unbind('failed', onDisconnected);
        } catch {
          /* ignore */
        }
      }
    };
  }, [uniqueUrl, restaurantId, pollIntervalMs, disabled, handleStatusChanged]);
}
